import { isLocationOrigin, MINUTE } from '@caresend/utils';
import isEqual from 'lodash.isequal';
import { getStore } from '../../../helpers/initStore.js';

/**
 * Options Fetching Helpers for Scheduling Flow
 *
 * This module manages the fetching state for two types of scheduling data:
 *
 * 1. SCHEDULING OPTIONS (Time slots for home visits)
 *    - Fetched after address entry
 *    - Can be prefetched asynchronously while user is still on address page
 *    - Should show loading overlay on Time step if still fetching when user arrives
 *    - Cached for 5 minutes to prevent unnecessary refetches
 *
 * 2. LAB APPOINTMENT OPTIONS (Available times at specific labs)
 *    - Fetched when a lab is selected (for labs without preset opening hours)
 *    - Always fetched asynchronously to keep labs selectable
 *    - Should NOT show loading on Time step (lab remains interactive)
 *    - Should show loading on Facility Times step if still fetching
 *    - Cached for 15 seconds (shorter duration for more real-time availability)
 *
 * KEY BEHAVIORS:
 * - Labs are ALWAYS selectable, even while appointment times are loading
 * - Loading overlays appear contextually based on the current step
 * - Async fetches allow for better UX by prefetching data before it's needed
 *
 * LOADING OVERLAY LOGIC:
 * - Time Step: Shows loading ONLY for scheduling options (not lab appointments)
 * - Facility Times Step: Shows loading if lab appointments are still fetching
 * - The showLoadingForAsyncFetch flag bridges navigation between steps
 */
/**
 * The threshold for when to reset scheduling options in the scheduling flow to
 * prevent stale options from being displayed.
 */
const RESET_SCHEDULING_OPTIONS_INTERVAL = 5 * MINUTE;
/**
 * The threshold for when to reset lab appointment times in the scheduling flow
 * to prevent stale times from being displayed.
 */
const RESET_LAB_APPOINTMENT_TIMES_INTERVAL = 15 * MINUTE;
/**
 * Returns the latest scheduling options fetch. This fetch is primarily used to
 * determine if we need to fetch new scheduling options.
 */
const getLatestSchedulingOptionsFetch = () => {
    const store = getStore();
    const { loadingSchedulingOptions } = store.state.schedulingFlow.localState;
    return Object.values(loadingSchedulingOptions !== null && loadingSchedulingOptions !== void 0 ? loadingSchedulingOptions : {}).sort((a, b) => b.timestamp - a.timestamp)[0];
};
const getLabAppointmentOptionsFetch = (placeID) => {
    const store = getStore();
    const { loadingLabAppointmentOptions } = store.state.schedulingFlow.localState;
    return loadingLabAppointmentOptions === null || loadingLabAppointmentOptions === void 0 ? void 0 : loadingLabAppointmentOptions[placeID];
};
/**
 * Used by the `schedulingFlow/getSchedulingOptions` action to determine if we
 * should begin fetching new scheduling options from the cloud or if we should
 * use the existing scheduling options.
 */
const getShouldGetNewSchedulingOptions = () => {
    const store = getStore();
    const newLocation = store.state.schedulingFlow.location;
    const isOrigin = isLocationOrigin(newLocation);
    const isValidLocation = newLocation && !isOrigin;
    const latestSchedulingOptionsFetch = getLatestSchedulingOptionsFetch();
    // If we don't have a previous fetch, we are on the address page on the mount of it
    // We'll only fetch if the location is valid
    if (!latestSchedulingOptionsFetch)
        return isValidLocation;
    const { location: lastLocation, isLoading, timestamp, } = latestSchedulingOptionsFetch;
    if (isLoading)
        return false;
    const hasSchedulingOptions = store.getters['schedulingFlow/getHasSchedulingOptions'];
    if (!hasSchedulingOptions)
        return true;
    const locationChanged = !isEqual(lastLocation, newLocation);
    if (locationChanged && isValidLocation)
        return true;
    const timeSinceLastFetch = timestamp ? Date.now() - timestamp : Infinity;
    const isTimeWithin5Minutes = timeSinceLastFetch < RESET_SCHEDULING_OPTIONS_INTERVAL;
    const areOptionsStale = isValidLocation && !isTimeWithin5Minutes;
    return areOptionsStale;
};
/**
 * Returns true if we should get new lab appointment times for a given lab.
 */
const getShouldGetNewLabAppointmentTimes = (selectedDrawPlaceID) => {
    const latestFetch = getLabAppointmentOptionsFetch(selectedDrawPlaceID);
    if (!latestFetch)
        return true;
    const { isLoading, timestamp } = latestFetch;
    if (isLoading)
        return false;
    const timeSinceLastFetch = timestamp ? Date.now() - timestamp : Infinity;
    const isTimeWithin15Minutes = timeSinceLastFetch < RESET_LAB_APPOINTMENT_TIMES_INTERVAL;
    const areTimesStale = !isTimeWithin15Minutes;
    return areTimesStale;
};

export { RESET_LAB_APPOINTMENT_TIMES_INTERVAL, RESET_SCHEDULING_OPTIONS_INTERVAL, getLabAppointmentOptionsFetch, getLatestSchedulingOptionsFetch, getShouldGetNewLabAppointmentTimes, getShouldGetNewSchedulingOptions };
